import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { environment as ENV } from '../../environments/environment';
import {
    DiplomaSupplementFileType, IDiplomaSupplement, IDiplomaSupplementAddModel, IDiplomaSupplementContactPerson,
    IDiplomaSupplementEditModel, IDiplomaSupplementListModel
} from '../models/DiplomaSupplement';
import { Utils } from '../core/Utils';
import { map } from 'rxjs/operators';
import { IPagingParams } from '../models/Common';

/**
 * Student diploma supplement service
 */
@Injectable({ providedIn: "root" })
export class DiplomaSupplementService {
    constructor(private http: HttpClient) { }

    private readonly apiUrl = `${ENV.apiUrl}/diplomaSupplements`;

    get(options?: {
        filter?: {
            program?: string,
            semester?: string,
            contactPerson?: string,
            search?: string
        },
        paging?: IPagingParams
    }): Observable<{ items: IDiplomaSupplementListModel[], total: number }> {
        return this.http.get<{ items: IDiplomaSupplementListModel[], total: number }>(this.apiUrl, {
            params: {
                ...Utils.sanitizeQueryParams(options?.filter),
                ...Utils.sanitizeQueryParams(options?.paging)
            }
        });
    }

    getById(id: number): Observable<IDiplomaSupplement> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.get<IDiplomaSupplement>(url);
    }

    find(personId: string, programCode: string): Observable<{ Id: number }> {
        const url = `${this.apiUrl}/find`;
        return this.http.get<{ Id: number }>(url, {
            params: {
                personId,
                programCode
            }
        });
    }

    getData(personId: string, programCode: string): Observable<IDiplomaSupplement> {
        const url = `${this.apiUrl}/data`;
        return this.http.get<IDiplomaSupplement>(url, {
            params: {
                personId,
                programCode
            }
        });
    }

    getFilters(): Observable<{ programs: string[], semesters: string[], contactPersons: string[] }> {
        const url = `${this.apiUrl}/filters`;
        return this.http.get<{ programs: string[], semesters: string[], contactPersons: string[] }>(url);
    }

    create(model: IDiplomaSupplementAddModel): Observable<number> {
        return this.http.post<number>(this.apiUrl, {
            ...model,
            Signed: Utils.getInputDateString(model.Signed)
        });
    }

    update(id: number, model: IDiplomaSupplementEditModel, isFinal: boolean = false): Observable<any> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.put<any>(url, {
            ...model,
            Signed: Utils.getInputDateString(model.Signed)
        }, {
            params: { isFinal }
        });
    }

    /**
     * Download diploma supplement attachment.
     * @param id
     * @param type
     */
    download(id: number, type: DiplomaSupplementFileType): Observable<any> {
        const url = `${this.apiUrl}/${id}/download`;
        return this.http.get(url, {
            params: { type },
            responseType: 'blob',
            observe: 'response'
        }).pipe(map(response => {
            Utils.downloadResponseFile(response);
            return of(undefined);
        }));
    }

    getContactPersons(): Observable<IDiplomaSupplementContactPerson[]> {
        const url = `${this.apiUrl}/contactPersons`;
        return this.http.get<IDiplomaSupplementContactPerson[]>(url);
    }

    setContactPerson(criteria: {
        id?: number[],
        program?: string,
        semester?: string,
        contactPerson?: string,
        search?: string
    }, contactPerson: string): Observable<any> {
        const url = `${this.apiUrl}/contactPersons`;
        return this.http.put(url, { criteria, contactPerson });
    }
}
